import { StatCard } from '@/components/stat/StatCard'
import type { KpiWidget } from '@/lib/cube-report'
import {
  changePercent,
  formatDateOnly,
  formatDecimal,
  formatNumberPattern,
  withCurrency,
} from '@/lib/format'
import { formatFieldValue } from '@/lib/cube-report'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

import { field } from './helpers'
import type { KpiResult, WidgetStatus } from './types'

/** Saf çizim: sonuç prop'tan gelir (canlı: `useKpiData`, kopya: dondurulmuş veri). */
export function KpiWidgetView({
  widget,
  data,
  status = {},
  fxFailed,
  printMode,
  onOpen,
}: {
  /** Verilirse kart düğmedir; tık kaynak satırları açar (`widget.drill`). */
  onOpen?: () => void
  widget: KpiWidget
  data: KpiResult | undefined
  status?: WidgetStatus
  /** Kur alınamadı (yalnız `widget.fx` için): ipucu satırı gösterilir. */
  fxFailed?: boolean
  /** Baskı: sayfa sonunda bölünmez. */
  printMode?: boolean
}) {
  const measure = field(widget, widget.measure)
  const current = data?.value

  let value: string
  let hint: string | undefined
  let change: number | undefined
  if (!widget.fx) {
    value = formatFieldValue(measure, current)
    if (data?.previous !== undefined) {
      hint = messages.report.previousPeriod(formatFieldValue(measure, data.previous))
      change = changePercent(current, data.previous)
    }
  } else if (!data?.fx) {
    value = messages.common.empty
    hint = fxFailed ? messages.report.fxUnavailable : undefined
  } else {
    const { rate, date } = data.fx
    const converted = current === null || current === undefined ? null : current * rate
    value = withCurrency(formatNumberPattern(converted, measure.numberFormat ?? '#,##0'), 'TL')
    hint = messages.report.fxRate(widget.fx, formatDateOnly(date), formatDecimal(rate, 4))
  }

  const card = (
    <StatCard
      label={widget.title ?? measure.label}
      busy={status.busy}
      hint={hint}
      change={change}
      className={cn(printMode && 'break-inside-avoid', onOpen && 'h-full')}
      // Yazdırma çıktısı sayaç geçişini beklemez: değer baştan yazılı.
      animate={!printMode}
      value={
        status.isLoading ? (
          <div className="bg-muted h-6 w-32 animate-pulse rounded-sm" />
        ) : status.isError ? (
          <p className="text-destructive text-sm font-normal">{messages.states.errorTitle}</p>
        ) : (
          value
        )
      }
    />
  )
  if (!onOpen || printMode) return card
  return (
    <button
      type="button"
      onClick={onOpen}
      title={messages.report.drillHint}
      aria-label={messages.report.drillLabel(widget.title ?? measure.label)}
      className="rounded-surface focus-visible:ring-ring block w-full cursor-pointer text-left transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
    >
      {card}
    </button>
  )
}
